const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

const DROP_WITH_CONTENTS = new Set([
  'embed',
  'form',
  'iframe',
  'input',
  'link',
  'meta',
  'object',
  'script',
  'style',
  'textarea',
]);

const GLOBAL_ATTRIBUTES = new Set(['class', 'title']);
const TAG_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'rel', 'target', 'title']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
};

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

// 预览 HTML 来自上传文件或服务端转换结果，不能信任；只保留文档预览需要的少量标签。
function escapeHtml(html: string) {
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isSafeHref(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return true;
  }

  try {
    return SAFE_URL_PROTOCOLS.has(new URL(trimmed, window.location.origin).protocol);
  } catch {
    return false;
  }
}

function sanitizeElement(element: Element, doc: Document): Node | null {
  const tagName = element.tagName.toLowerCase();

  if (DROP_WITH_CONTENTS.has(tagName)) {
    return null;
  }

  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    Array.from(element.childNodes).forEach((child) => {
      const sanitized = sanitizeNode(child, doc);
      if (sanitized) fragment.appendChild(sanitized);
    });
    return fragment;
  }

  const clean = doc.createElement(tagName);
  const tagAttributes = TAG_ATTRIBUTES[tagName];

  Array.from(element.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    const value = attribute.value;

    if (name.startsWith('on') || name === 'style') {
      return;
    }

    if (!GLOBAL_ATTRIBUTES.has(name) && !tagAttributes?.has(name)) {
      return;
    }

    if (tagName === 'a' && name === 'href' && !isSafeHref(value)) {
      return;
    }

    clean.setAttribute(name, value);
  });

  if (tagName === 'a' && clean.getAttribute('target') === '_blank') {
    clean.setAttribute('rel', 'noopener noreferrer');
  }

  Array.from(element.childNodes).forEach((child) => {
    const sanitized = sanitizeNode(child, doc);
    if (sanitized) clean.appendChild(sanitized);
  });

  return clean;
}

function sanitizeNode(node: Node, doc: Document): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent ?? '');
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    return sanitizeElement(node as Element, doc);
  }

  return null;
}

export function sanitizePreviewHtml(html: string) {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // 浏览器 API 不存在时退化为纯文本，避免把原始 HTML 透传到 sink。
    return escapeHtml(html);
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(html, 'text/html');
  const clean = document.implementation.createHTMLDocument('');

  Array.from(parsed.body.childNodes).forEach((child) => {
    const sanitized = sanitizeNode(child, clean);
    if (sanitized) clean.body.appendChild(sanitized);
  });

  return clean.body.innerHTML;
}
